import React, { useCallback, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService } from "../services/api";
import ImageCreation from "./image_creation/image_creation";
import { settlePendingVideoCredit } from "../utils/videoCreditSettlement";
import "./my_creations.scss";

const API_ORIGIN = "https://api.zulluai.com";

const getUserIdentifier = () => {
  const rawId =
    localStorage.getItem("phoneNumber") || localStorage.getItem("userEmail");

  if (!rawId) return "";
  return rawId.includes("@") || rawId.startsWith("+") ? rawId : `+${rawId}`;
};

const getAssetUrl = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_ORIGIN}${url}`;
};

const getVideoStatusState = (status, videoUrl) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "completed" && videoUrl) {
    return { className: "completed", label: "Completed" };
  }

  if (
    [
      "pending",
      "processing",
      "queued",
      "running",
      "in_queue",
      "in_progress",
      "dreaming",
      "created",
      "waiting",
      "submitted",
    ].includes(normalizedStatus)
  ) {
    return { className: "processing", label: "Generating..." };
  }

  if (normalizedStatus === "completed" && !videoUrl) {
    return { className: "processing", label: "Finalizing..." };
  }

  if (
    ["failed", "fail", "error", "rejected", "cancelled", "canceled"].includes(
      normalizedStatus
    )
  ) {
    return { className: "failed", label: "Failed" };
  }

  return { className: "processing", label: "Processing..." };
};

const MyCreations = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(
    type === "videos" || type === "images" ? type : null
  );

  const settleCompletedVideoCredits = useCallback((items) => {
    items.forEach((video) => {
      const statusState = getVideoStatusState(video.status, video.videoUrl);
      const jobId = video.jobId || video.job_id || video.id;
      if (statusState.className === "completed" && jobId) {
        settlePendingVideoCredit(jobId);
      }
    });
  }, []);

  const fetchHistory = useCallback(
    async ({ silent = false } = {}) => {
      const formattedId = getUserIdentifier();
      if (!formattedId) {
        setLoading(false);
        return;
      }

      if (!silent) setLoading(true);

      try {
        const response = await ApiService.getUserHistory(formattedId);
        const nextVideos = Array.isArray(response)
          ? response
          : response?.data && Array.isArray(response.data)
          ? response.data
          : [];
        setVideos(nextVideos);
        settleCompletedVideoCredits(nextVideos);
      } catch (err) {
        console.error("Failed to load history", err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [settleCompletedVideoCredits]
  );

  useEffect(() => {
    setActiveType(type === "videos" || type === "images" ? type : null);
  }, [type]);

  const openType = (nextType) => {
    navigate(`/my-creations/${nextType}`);
  };

  const closeType = () => {
    navigate("/my-creations");
  };

  useEffect(() => {
    if (activeType !== "videos") return undefined;

    fetchHistory();
    const refreshTimer = setInterval(() => {
      fetchHistory({ silent: true });
    }, 8000);

    return () => clearInterval(refreshTimer);
  }, [activeType, fetchHistory]);

  const handleDownload = async (assetUrl, filename) => {
    try {
      const response = await fetch(assetUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "zullu-creation.mp4");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(assetUrl, "_blank");
    }
  };

  const renderPicker = () => (
    <div className="creation-type-grid">
      <button
        className="creation-type-card video-choice"
        onClick={() => openType("videos")}
      >
        <span className="type-icon">
          <i className="fi fi-rr-video-camera"></i>
        </span>
        <span className="type-copy">
          <b>Videos</b>
          <small>Review generations, status, and downloads.</small>
        </span>
        <i className="fi fi-rr-arrow-small-right type-arrow"></i>
      </button>

      <button
        className="creation-type-card image-choice"
        onClick={() => openType("images")}
      >
        <span className="type-icon">
          <i className="fi fi-rr-picture"></i>
        </span>
        <span className="type-copy">
          <b>Images</b>
          <small>Browse your saved image creations.</small>
        </span>
        <i className="fi fi-rr-arrow-small-right type-arrow"></i>
      </button>
    </div>
  );

  const renderVideos = () => {
    if (loading) {
      return (
        <div className="loader-container">
          <p>Fetching your gallery...</p>
        </div>
      );
    }

    if (!videos.length) {
      return <div className="no-videos">No videos found.</div>;
    }

    return (
      <div className="video-grid">
        {videos.map((vid) => {
          const videoUrl = getAssetUrl(vid.videoUrl);
          const statusState = getVideoStatusState(vid.status, vid.videoUrl);
          return (
            <div key={vid._id} className="video-card">
              <div className="video-wrapper">
                {statusState.className === "completed" ? (
                  <video src={videoUrl} controls playsInline />
                ) : (
                  <div className={`status-overlay ${statusState.className}`}>
                    <span>{statusState.label}</span>
                  </div>
                )}
              </div>
              <div className="video-info">
                <p className="prompt-text">{vid.prompt}</p>
                <div className="card-footer">
                  <span className="date-tag">
                    {new Date(vid.createdAt).toLocaleDateString()}
                  </span>
                  {statusState.className === "completed" && (
                    <button
                      className="download-btn-action"
                      onClick={() =>
                        handleDownload(videoUrl, `zullu_${vid.jobId}.mp4`)
                      }
                      aria-label="Download video"
                    >
                      <i className="fi fi-rr-download"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="history-container">
      <div className="history-header">
        {activeType && (
          <button
            className="back-to-types"
            onClick={closeType}
            aria-label="Back to creation types"
          >
            <i className="fi fi-rr-arrow-small-left"></i>
          </button>
        )}
        <div>
          <h2>Your Creations</h2>
          <p>
            {activeType === "videos"
              ? "Manage and download your AI-generated videos"
              : activeType === "images"
              ? "Explore and download your AI-generated images"
              : "Choose the gallery you want to open"}
          </p>
        </div>
      </div>

      {!activeType && renderPicker()}
      {activeType === "videos" && renderVideos()}
      {activeType === "images" && <ImageCreation />}
    </div>
  );
};

export default MyCreations;
